import { createFileRoute } from '@tanstack/react-router';
import { RESOURCE_KINDS, type ResourceKind } from '@leanin/shared/resource-kinds';
import { AdminResourcesView } from '@/components/admin/AdminResourcesView';
import type {
  AdminResourceContentLayer,
  AdminResourceStatus,
  ListAdminResourcesInput,
} from '@/lib/queries/admin';

interface ResourcesSearch {
  readonly status?: AdminResourceStatus;
  readonly q?: string;
  readonly layer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly cursor?: string;
}

const ALLOWED_STATUS: ReadonlyArray<AdminResourceStatus> = [
  'all',
  'draft',
  'published',
  'featured',
];

const ALLOWED_LAYER: ReadonlyArray<AdminResourceContentLayer> = [
  'community',
  'network',
  'public',
];

const ALLOWED_KIND: ReadonlyArray<ResourceKind> = RESOURCE_KINDS;

export const Route = createFileRoute('/_authed/admin/resources/')({
  validateSearch: (raw: Record<string, unknown>): ResourcesSearch => {
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_STATUS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as AdminResourceStatus)
        : undefined;
    const q = typeof raw.q === 'string' && raw.q.length > 0 ? raw.q : undefined;
    const layer =
      typeof raw.layer === 'string' &&
      (ALLOWED_LAYER as ReadonlyArray<string>).includes(raw.layer)
        ? (raw.layer as AdminResourceContentLayer)
        : undefined;
    const kind =
      typeof raw.kind === 'string' &&
      (ALLOWED_KIND as ReadonlyArray<string>).includes(raw.kind)
        ? (raw.kind as ResourceKind)
        : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    return {
      ...(status !== undefined ? { status } : {}),
      ...(q !== undefined ? { q } : {}),
      ...(layer !== undefined ? { layer } : {}),
      ...(kind !== undefined ? { kind } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    };
  },
  component: AdminResourcesPage,
});

function AdminResourcesPage() {
  const search = Route.useSearch();
  const activeStatus: AdminResourceStatus = search.status ?? 'all';
  const input: ListAdminResourcesInput = {
    pageSize: 25,
    status: activeStatus,
    ...(search.layer ? { contentLayer: search.layer } : {}),
    ...(search.kind ? { kind: search.kind } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return <AdminResourcesView input={input} searchParams={search} />;
}
